import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { Firestore, getFirestore } from 'firebase/firestore';
import { catchError, from, map, Observable, switchMap, tap, throwError } from 'rxjs';
import { StateService } from './state.service';
import { EnvConfigService } from './env-config.service';

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  private readonly http = inject(HttpClient);
  private readonly stateService = inject(StateService);
  private readonly env = inject(EnvConfigService);

  private firestore!: Firestore;

  // get the token at the app initialization and save it in the state service
  getFirebaseAuthToken() {
    return this.http.post(`${this.env.getConfig().apiUrl}/firestore/cloud-documents/mobile-custom-token`, {});
  }

  getFirebaseConfig() {
    return this.http.get(`${this.env.getConfig().apiUrl}/firestore/config/${this.env.getConfig().isLocal}`);
  }

  initializeFirebase(): Observable<void> {
    return this.getFirebaseConfig().pipe(
      // Extract configuration and initialize Firebase app
      tap((response: any) => {
        const app = initializeApp(response.data);
        this.firestore = getFirestore(app);
      }),
      // Get Firebase authentication token
      switchMap(() => this.getFirebaseAuthToken()),
      map((tokenResponse: any) => tokenResponse.message),
      tap((token: string) => {
        this.stateService.setAuthToken(token);
      }),
      // Sign in with custom token
      switchMap((token: string) => this.signInWithCustomTokenO(token)),
      tap((authResponse: any) => {
        this.stateService.setFirebaseUser(authResponse.user);
      }),
      // Handle any errors during initialization
      catchError(error => {
        console.error('Error during firebase initialization:', error);
        return throwError(() => error);
      }),
      map(() => {
        return;
      }),
    );
  }

  signInWithCustomToken(customToken: string): Promise<{ user: any; db: Firestore }> {
    const auth = getAuth();
    return signInWithCustomToken(auth, customToken).then(userCredential => ({
      user: userCredential.user,
      db: this.firestore,
    }));
  }

  signInWithCustomTokenO(customToken: string): Observable<{ user: any; db: Firestore }> {
    const auth = getAuth();
    return from(
      signInWithCustomToken(auth, customToken).then(userCredential => ({
        user: userCredential.user,
        db: this.firestore,
      })),
    );
  }
}
